import { useState } from "react";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import { NavLink, Outlet, Navigate, useLocation } from "react-router-dom";

import { Button } from "@/components/ui/Button";
import { SECTION_PERMISSIONS } from "@/config/permissions";
import { usePermissions } from "@/features/auth/usePermissions";
import { extractApiError } from "@/lib/api-error";

import { exportRideBookingsTripsWorkbook } from "../export/exportRideBookingsTripsWorkbook";

const tabs = [
    {
        label: "Ride Offers",
        path: "/ride-bookings-trips/rides",
        permissions: SECTION_PERMISSIONS.RIDE_OFFERS,
    },
    {
        label: "Bookings",
        path: "/ride-bookings-trips/bookings",
        permissions: SECTION_PERMISSIONS.RIDE_BOOKINGS,
    },
    {
        label: "Trips",
        path: "/ride-bookings-trips/trips",
        permissions: SECTION_PERMISSIONS.TRIPS,
    },
];

export const RideBookingsTripsLayout = () => {
    const location = useLocation();
    const { hasAllPermissions, hasAnyPermission } = usePermissions();
    const [isExporting, setIsExporting] = useState(false);
    const [exportError, setExportError] = useState("");
    const visibleTabs = tabs.filter((tab) => hasAnyPermission(tab.permissions));
    const canExportWorkbook = hasAllPermissions([
        ...SECTION_PERMISSIONS.RIDE_OFFERS,
        ...SECTION_PERMISSIONS.RIDE_BOOKINGS,
        ...SECTION_PERMISSIONS.TRIPS,
    ]);
    const firstVisibleTab = visibleTabs[0]?.path ?? "/ride-bookings-trips/rides";

    // Redirect index to rides
    if (location.pathname === "/ride-bookings-trips" || location.pathname === "/ride-bookings-trips/") {
        return <Navigate to={firstVisibleTab} replace />;
    }

    // Hide sub-nav on detail pages (paths with more segments)
    const isDetailPage = /^\/ride-bookings-trips\/(rides|bookings|trips)\/[^/]+/.test(location.pathname);

    const handleExport = async () => {
        try {
            setIsExporting(true);
            setExportError("");
            await exportRideBookingsTripsWorkbook();
        } catch (error) {
            setExportError(extractApiError(error));
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <div className="space-y-6">
            {!isDetailPage && (
                <div>
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Ride Bookings & Trips</h1>
                            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Manage ride offers, bookings, and trips across the platform.</p>
                        </div>
                        {canExportWorkbook ? (
                            <Button onClick={handleExport} disabled={isExporting}>
                                <ArrowDownTrayIcon className="mr-1.5 h-4 w-4" />
                                {isExporting ? "Exporting..." : "Export Excel"}
                            </Button>
                        ) : null}
                    </div>
                    {exportError ? (
                        <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300">
                            {exportError}
                        </div>
                    ) : null}
                    <div className="mt-4 flex gap-1 border-b border-slate-200 dark:border-slate-800">
                        {visibleTabs.map((tab) => (
                            <NavLink
                                key={tab.path}
                                to={tab.path}
                                className={({ isActive }) =>
                                    `relative px-4 py-2.5 text-sm font-medium transition-colors ${isActive
                                        ? "text-primary-600 dark:text-primary-400"
                                        : "text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        {tab.label}
                                        {isActive && (
                                            <span className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-primary-600 dark:bg-primary-400" />
                                        )}
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </div>
                </div>
            )}
            <Outlet />
        </div>
    );
};
